/* eslint-disable camelcase */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'

import Action from 'ageas-react-component-action'
import CheckBox from 'ageas-react-component-checkbox'
import Dropdown from 'ageas-react-component-dropdownlist'
import Link from 'ageas-react-component-link'
import MainLoader from 'ageas-react-component-loader'
import RadioButton from 'ageas-react-component-radiobutton'
import Section from 'ageas-react-component-section'
import TextField from 'ageas-react-component-textfield'

import { Plus } from '../../Components/Icons'
import { LegalInformation } from '../../Components/LegalInformation'
import { MainContainer } from '../../GlobalStyles'
import { usePaymentConfirmationStore } from '../../Stores/paymentConfirmationStore'
import { usePaymentStore } from '../../Stores/paymentStore'
import { useProgressStore } from '../../Stores/progressStore'
import { useAcceptConditionsStore } from '../../Stores/useAcceptConditionsStore'
import { useConsentsStore } from '../../Stores/useConsentsStore'
import { useWarningStore } from '../../Stores/warningStore'

import IBAN from 'iban'
import { useCreatePolicyHolderEntityStore } from '../../Stores/useCreatePolicyholderEntityStore'
import { useGetAdditionalEntityDetailsStore } from '../../Stores/useGetAdditionalEntityDetailsStore'
import { useResumeStore } from '../../Stores/useResumeStore'
import { useSubmitProposalStore } from '../../Stores/useSubmitProposalStore'
import { OPTIONS_YES_NO } from '../../constants/optionsYesNo'
import { ROUTES } from '../../constants/routes'
import { useSubmitProposal } from '../../hooks/api/submitProposal/useSubmitProposal'
import { useSubmitProposalPayload } from '../../hooks/api/submitProposal/useSubmitProposalPayload'
import { t } from '../../i18n'
import { AddNib } from './AddNib'
import { Consents } from './Consents'
import {
  CheckBoxContainer,
  Container,
  FormalizationContainer,
  FormalizationTableContainer,
  PlusLinkContainer,
  RadioButtonContainer,
} from './style'

export type FormValuesFormalizationProps = {
  iban: string
  signed: string
  documentation: string
  docDigital: string
  email: string
}

const elementPortal = document.getElementsByTagName('body')?.[0]

export const Formalization = () => {
  const navigate = useNavigate()
  const { setCurrentStep } = useProgressStore()
  const { isSubmitProposalRequested } = useSubmitProposalStore()
  const { directDebit, addedIbanList, setSelectedIBAN, selectedIBAN } =
    usePaymentStore()
  const { isSignatureDigital, setIsSignatureDigital } =
    usePaymentConfirmationStore()
  const { isPolicyholderCreated } = useCreatePolicyHolderEntityStore()
  const { acceptConditions } = useAcceptConditionsStore()
  const { policyholderAdditionalDetails } = useGetAdditionalEntityDetailsStore()
  const { setWarningOptions } = useWarningStore()
  const { disabledStepsByResume } = useResumeStore()
  const [addNibModal, setAddNibModal] = useState(false)
  const [showEmail, setShowEmail] = useState(
    isPolicyholderCreated || acceptConditions?.isPaperless || true,
  )
  const { consents } = useConsentsStore()
  const { handleSubmitProposalPayload } = useSubmitProposalPayload()

  const [formValues, setFormValues] = useState<FormValuesFormalizationProps>({
    iban: selectedIBAN || '',
    signed: isSignatureDigital ? OPTIONS_YES_NO.yes : OPTIONS_YES_NO.no,
    documentation: t('pages.step6.digital').toLowerCase(),
    docDigital: '1',
    email: policyholderAdditionalDetails?.emailContact?.email || '',
  })

  const { isLoading, isSuccess, mutate } = useSubmitProposal()

  const formatterIban =
    acceptConditions?.ibanList?.map((iban) => ({
      caption: iban,
      value: iban,
    })) || []

  const formatterAddedIban =
    addedIbanList.map((iban) => ({
      caption: iban,
      value: iban,
    })) || []

  const ibanList = [...formatterIban, ...formatterAddedIban]

  const handleFormValues = (value: any) => {
    setFormValues((prevState) => ({
      ...prevState,
      ...value,
    }))
  }

  const handleNavigate = () => {
    navigate(ROUTES.confirmation)
  }

  const validadeConsents = () => {
    return consents.every((_consent) => {
      return !_consent.mandatory || _consent.answer
    })
  }

  const handleContinueClick = () => {
    if (isSubmitProposalRequested) {
      handleNavigate()
      return
    }

    if (validadeConsents()) {
      mutate(handleSubmitProposalPayload(formValues))
    } else {
      setWarningOptions({
        labelTypes: [t('pages.step6.errors.consents')],
      })
    }
  }

  useEffect(() => {
    setCurrentStep(6)
  }, [])

  useEffect(() => {
    if (ibanList.length > 0) {
      setSelectedIBAN(ibanList[0].value)
    }
  }, [])

  useEffect(() => {
    if (isSuccess) {
      handleNavigate()
    }
  }, [isSuccess, navigate])

  return (
    <>
      <MainLoader show={isLoading} />
      <MainContainer>
        <FormalizationContainer>
          {Boolean(Number(directDebit)) && (
            <>
              <div className="section">
                <Section
                  id="f-payment-data"
                  config={{
                    labelSize: '16px',
                    labelBold: '500',
                    labelTransform: 'uppercase',
                  }}
                  name={t('pages.step6.paymentData')}
                  colorTheme="blue"
                />
              </div>
              <div className="row">
                <div className="col-large">
                  <TextField
                    readOnly
                    id="f-direct-debit"
                    name="f-direct-debit"
                    label={t('pages.step6.paymentType')}
                    value={t('pages.step6.directDebit')}
                    colorTheme="blue"
                    version="2"
                  />
                </div>
                <div className="col-small">
                  <Dropdown
                    readOnly={disabledStepsByResume.step6}
                    id={`f-iban${selectedIBAN}`}
                    key={`f-iban${selectedIBAN}`}
                    name="iban"
                    label={t('pages.step6.ownerIBAN')}
                    colorTheme="blue"
                    value={selectedIBAN}
                    options={ibanList}
                    version="2"
                    onValueChanged={(id: string, value: string) => {
                      if (ibanList.map((item) => item.value).includes(value)) {
                        if (IBAN.isValid(value)) {
                          handleFormValues({ iban: value })
                          setSelectedIBAN(value)
                        }
                      }
                    }}
                  />
                  {!disabledStepsByResume.step6 && (
                    <PlusLinkContainer>
                      <Plus color="#12a3bb" size={24} />
                      <Link
                        label={t('pages.step6.addBank')}
                        onValueChanged={() => setAddNibModal(true)}
                      />
                    </PlusLinkContainer>
                  )}
                </div>
              </div>
            </>
          )}
          <div className="section">
            <Section
              id="f-signature"
              config={{
                labelSize: '16px',
                labelBold: '500',
                labelTransform: 'uppercase',
              }}
              name={t('pages.step6.signatureLegalDocument')}
              colorTheme="blue"
            />
          </div>
          <div className="row">
            <div className="col-large">
              <RadioButtonContainer>
                <RadioButton
                  id="f-signature-radio"
                  label={t('pages.step6.signatureType')}
                  version="2"
                  value={formValues.signed}
                  readOnly={disabledStepsByResume.step6}
                  mode="Save"
                  icon={null}
                  onValueChanged={(id: string, value: string) => {
                    if (value === OPTIONS_YES_NO.yes) {
                      setIsSignatureDigital(true)
                    } else {
                      setIsSignatureDigital(false)
                    }
                    handleFormValues({ signed: value })
                  }}
                  options={[
                    {
                      caption: t('pages.step6.digital'),
                      value: OPTIONS_YES_NO.yes,
                    },
                    {
                      caption: t('pages.step6.paper'),
                      value: OPTIONS_YES_NO.no,
                    },
                  ]}
                />
              </RadioButtonContainer>
            </div>
          </div>
          <div className="section">
            <Section
              id="f-method"
              config={{
                labelSize: '16px',
                labelBold: '500',
                labelTransform: 'uppercase',
              }}
              name={t('pages.step6.documentSendMethod')}
              colorTheme="blue"
            />
          </div>
          <div className="row flex-end">
            <div className="col-large">
              <RadioButtonContainer>
                <RadioButton
                  id="f-documentation"
                  label={t('pages.step6.documentSend')}
                  version="2"
                  readOnly={disabledStepsByResume.step6}
                  value={formValues.documentation}
                  mode="Save"
                  icon={null}
                  onValueChanged={(id: string, value: string) => {
                    handleFormValues({ documentation: value })
                  }}
                  options={[
                    {
                      caption: t('pages.step6.deliveryPerson'),
                      value: t('pages.step6.digital').toLowerCase(),
                    },
                    {
                      caption: t('pages.step6.deliveryAgeas'),
                      value: t('pages.step6.paper').toLowerCase(),
                    },
                  ]}
                />
              </RadioButtonContainer>
              <div className="space"></div>
              <CheckBoxContainer>
                <CheckBox
                  id="f-docDigital"
                  readOnly={
                    acceptConditions?.isPaperless || disabledStepsByResume.step6
                  }
                  value={formValues.docDigital}
                  label={t('pages.step6.joinDigitalDocument')}
                  onValueChanged={(key: string, value: string) => {
                    handleFormValues({ docDigital: value })
                    setShowEmail(Boolean(Number(value)) as never)
                  }}
                />
              </CheckBoxContainer>
            </div>
            <div className="col-small">
              {showEmail && (
                <TextField
                  required
                  readOnly={disabledStepsByResume.step6}
                  id="f-email"
                  name="email"
                  label={t('pages.step6.emailJoinDigitalDocument')}
                  value={formValues.email}
                  colorTheme="blue"
                  onValueChanged={(key: any, value: any) =>
                    handleFormValues({ email: value })
                  }
                  version="2"
                />
              )}
            </div>
          </div>
        </FormalizationContainer>
        {Boolean(acceptConditions?.consentList?.[0].legalConsents) && (
          <FormalizationTableContainer>
            <Consents />
          </FormalizationTableContainer>
        )}
        <Container>
          <div className="legal-container">
            {(acceptConditions?.consentList || [])?.map((consent) => (
              <>
                {(consent?.informativeNotes || [])?.map((informativeNote) => (
                  <LegalInformation
                    data={{
                      title: informativeNote.title || '',
                      largeDescription: informativeNote.largeDescription || '',
                      shortDescription: `${(
                        informativeNote.largeDescription || ''
                      ).substring(0, 138)}...`,
                    }}
                  />
                ))}
              </>
            ))}
          </div>
          <Action
            id="action1"
            onValueChanged={() => {
              handleContinueClick()
            }}
            colorTheme="blue"
            buttonType="primary"
            label={t('general.continue')}
            disable={
              (Boolean(Number(directDebit)) && selectedIBAN?.length === 0) ||
              (showEmail && formValues.email.length === 0) ||
              disabledStepsByResume.step6
            }
          />

          {createPortal(
            <AddNib
              isVisible={addNibModal}
              closeAction={() => setAddNibModal(false)}
            />,
            elementPortal!,
          )}
        </Container>
      </MainContainer>
    </>
  )
}
